"use client"

import { useEffect, useState } from "react"
import { COLOR_OPTIONS } from "@/schema/colors"
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { ArrowLeft, Check, Shuffle, Sparkles } from "lucide-react"

const HEX_REGEX = /^#?[0-9A-Fa-f]{6}$/

function normalizeHex(input: string): string | null {
  const trimmed = input.trim()
  if (!HEX_REGEX.test(trimmed)) return null
  return `#${trimmed.replace(/^#/, "").toUpperCase()}`
}

function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360
  s /= 100
  l /= 100
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * c)
      .toString(16)
      .padStart(2, "0")
  }
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase()
}

// Random palette of 12 colors. Lightness clamped to 50–65 to stay readable on
// both light and dark Badge backgrounds.
function generatePalette(): string[] {
  const base = Math.random() * 360
  const offsets = [0, 15, 30, 60, 90, 120, 150, 180, 210, 240, 285, 330]
  return offsets.map((offset) => {
    const h = base + offset + (Math.random() - 0.5) * 14
    const s = 60 + Math.random() * 20
    const l = 50 + Math.random() * 15
    return hslToHex(h, s, l)
  })
}

interface ColorPickerProps {
  value?: string | null
  onChange: (hex: string) => void
  trigger: React.ReactNode
  align?: "start" | "center" | "end"
  side?: "top" | "right" | "bottom" | "left"
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ColorPicker({
  value,
  onChange,
  trigger,
  align = "start",
  side = "bottom",
  open: controlledOpen,
  onOpenChange,
}: ColorPickerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next)
    else setUncontrolledOpen(next)
  }

  const [mode, setMode] = useState<"presets" | "generated">("presets")
  const [generated, setGenerated] = useState<string[]>(() => generatePalette())
  const [generationKey, setGenerationKey] = useState(0)
  const [hexInput, setHexInput] = useState(value ?? "")
  const [hexError, setHexError] = useState(false)

  useEffect(() => {
    if (open) {
      setHexInput(value ?? "")
      setHexError(false)
      setMode("presets")
    }
  }, [open, value])

  const commit = (hex: string) => {
    onChange(hex)
    setHexInput(hex)
    setHexError(false)
    setOpen(false)
  }

  const handleHexCommit = () => {
    if (!hexInput.trim()) {
      setHexError(false)
      return
    }
    const normalized = normalizeHex(hexInput)
    if (!normalized) {
      setHexError(true)
      return
    }
    commit(normalized)
  }

  const normalizedInput = normalizeHex(hexInput)
  const isHexDirty = hexInput.trim() !== "" && normalizedInput !== (value ?? null)

  const swatches =
    mode === "presets"
      ? Object.entries(COLOR_OPTIONS).map(([name, hex]) => ({ key: name, hex }))
      : generated.map((hex, i) => ({ key: `gen-${i}`, hex }))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-56 p-3" align={align} side={side}>
        <div className="space-y-3">
          <div key={`${mode}-${generationKey}`} className="grid grid-cols-4 gap-2">
            {swatches.map(({ key, hex }, i) => (
              <button
                key={key}
                type="button"
                onClick={() => commit(hex)}
                className={cn(
                  "animate-in fade-in zoom-in-50 fill-mode-both h-9 rounded-md ring-2 ring-offset-2 transition-all hover:scale-110",
                  (normalizedInput ?? value) === hex ? "ring-primary" : "ring-transparent"
                )}
                style={{
                  backgroundColor: hex,
                  animationDelay: `${i * 30}ms`,
                  animationDuration: "300ms",
                }}
                aria-label={`Select ${hex}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-1">
            <div className="relative min-w-0 flex-1">
              <label
                className="absolute top-1/2 left-1.5 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center overflow-hidden rounded border border-neutral-200"
                style={{ backgroundColor: normalizedInput ?? value ?? "transparent" }}
                aria-label="Open native color picker"
              >
                <input
                  type="color"
                  value={normalizedInput ?? value ?? "#000000"}
                  onChange={(e) => {
                    setHexInput(e.target.value.toUpperCase())
                    setHexError(false)
                  }}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
              </label>
              <Input
                value={hexInput}
                onChange={(e) => {
                  setHexInput(e.target.value)
                  setHexError(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleHexCommit()
                  }
                }}
                placeholder="#RRGGBB"
                className={cn(
                  "h-8 pr-8 pl-8 font-mono text-xs",
                  hexError && "border-red-accent focus-visible:ring-red-accent"
                )}
              />
              {isHexDirty && (
                <Button
                  type="button"
                  variant="primary"
                  size="iconXs"
                  disableInteraction
                  onClick={handleHexCommit}
                  aria-label="Save color"
                  className="animate-in fade-in zoom-in-75 absolute top-1/2 right-1 size-5 -translate-y-1/2 duration-200 [&_svg]:size-3"
                >
                  <Check />
                </Button>
              )}
            </div>
            {mode === "presets" ? (
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                disableInteraction
                onClick={() => {
                  setMode("generated")
                  setGenerated(generatePalette())
                  setGenerationKey((k) => k + 1)
                }}
                aria-label="Generate colors"
              >
                <Sparkles />
              </Button>
            ) : (
              <div
                key={`actions-${generationKey}`}
                className="animate-in fade-in slide-in-from-bottom-1 fill-mode-both flex items-center gap-1 duration-300"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="iconSm"
                  disableInteraction
                  onClick={() => {
                    setMode("presets")
                    setGenerationKey((k) => k + 1)
                  }}
                  aria-label="Back to presets"
                >
                  <ArrowLeft />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="iconSm"
                  disableInteraction
                  onClick={() => {
                    setGenerated(generatePalette())
                    setGenerationKey((k) => k + 1)
                  }}
                  aria-label="Shuffle"
                >
                  <Shuffle />
                </Button>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
